/**
 * Collateral Policy API Service
 *
 * Type-safe API client for collateral policy management.
 * Types derived from collateral-policy.openapi.yaml.
 *
 * Endpoints consumed:
 *   GET    /collateral-policy/:tenantId            - Get tenant policy
 *   PUT    /collateral-policy/:tenantId            - Update tenant policy
 *   GET    /collateral-policy/:tenantId/asset-classes  - List asset class policies
 *   PUT    /collateral-policy/:tenantId/asset-classes/:taxonomyNodeId  - Update single
 *   POST   /collateral-policy/:tenantId/validate   - Validate (dry-run)
 *   GET    /collateral-policy/:tenantId/audit      - Audit trail
 *   GET    /collateral-policy/baselines            - Get baseline
 */

import createClient from 'openapi-fetch';
import type { paths } from '@aaimplatform/institutional-types/generated/collateral-policy';
import type { components } from '@aaimplatform/institutional-types/generated/collateral-policy';
import { authMiddleware } from '../client';
import { apiErrorMessage } from '../error-message';
// platform-admin's authMiddleware is a factory; admin-portal's was a direct
// object. Both inject the Bearer token + bounce on auth-broken responses.

export type CollateralPolicy = components['schemas']['CollateralPolicy'];
export type CollateralPolicyUpdate = components['schemas']['CollateralPolicyUpdate'];
/** A full save names the version it was composed from (R1c4c-f1); null when no policy is saved. */
export type CollateralPolicySave = components['schemas']['CollateralPolicySave'];
export type AssetClassPolicy = components['schemas']['AssetClassPolicy'];
export type AssetClassPolicyUpdate = components['schemas']['AssetClassPolicyUpdate'];
export type BaselinePolicy = components['schemas']['BaselinePolicy'];
export type PolicyAuditEntry = components['schemas']['PolicyAuditEntry'];
export type PolicyValidationResult = components['schemas']['PolicyValidationResult'];
export type PolicyViolation = components['schemas']['PolicyViolation'];
export type PolicyWarning = components['schemas']['PolicyWarning'];

function createCollateralPolicyClient() {
  const client = createClient<paths>({ baseUrl: '/api/v1', credentials: 'include' });
  client.use(authMiddleware());
  return client;
}

const client = createCollateralPolicyClient();

export const collateralPolicyApi = {
  getPolicy: async (tenantId: string): Promise<CollateralPolicy | null> => {
    const { data, error, response } = await client.GET('/collateral-policy/{tenantId}', {
      params: { path: { tenantId } },
    });
    if (response.status === 404) return null;
    if (error) throw new Error('Failed to fetch collateral policy');
    return data as CollateralPolicy;
  },

  updatePolicy: async (tenantId: string, policy: CollateralPolicySave): Promise<CollateralPolicy> => {
    const { data, error } = await client.PUT('/collateral-policy/{tenantId}', {
      params: { path: { tenantId } },
      body: policy,
    });
    if (error) {
      if ('violations' in error && error.violations) {
        throw Object.assign(new Error('Policy validation failed'), { violations: error.violations });
      }
      throw new Error('Failed to update collateral policy');
    }
    return data as CollateralPolicy;
  },

  getAssetClasses: async (tenantId: string): Promise<AssetClassPolicy[]> => {
    const { data, error } = await client.GET('/collateral-policy/{tenantId}/asset-classes', {
      params: { path: { tenantId } },
    });
    if (error) throw new Error('Failed to fetch asset class policies');
    return data?.assetClasses ?? [];
  },

  updateAssetClass: async (
    tenantId: string,
    taxonomyNodeId: AssetClassPolicy['taxonomyNodeId'],
    update: AssetClassPolicyUpdate,
  ): Promise<AssetClassPolicy> => {
    const { data, error } = await client.PUT('/collateral-policy/{tenantId}/asset-classes/{taxonomyNodeId}', {
      params: { path: { tenantId, taxonomyNodeId } },
      body: update,
    });
    if (error) throw new Error('Failed to update asset class policy');
    return data as AssetClassPolicy;
  },

  validatePolicy: async (tenantId: string, policy: CollateralPolicyUpdate): Promise<PolicyValidationResult> => {
    const { data, error } = await client.POST('/collateral-policy/{tenantId}/validate', {
      params: { path: { tenantId } },
      body: policy,
    });
    if (error) throw new Error('Failed to validate policy');
    return data as PolicyValidationResult;
  },

  getAuditTrail: async (
    tenantId: string,
    limit = 25,
    offset = 0,
  ): Promise<{ entries: PolicyAuditEntry[]; total: number }> => {
    const { data, error } = await client.GET('/collateral-policy/{tenantId}/audit', {
      params: { path: { tenantId }, query: { limit, offset } },
    });
    if (error) throw new Error('Failed to fetch audit trail');
    return data as { entries: PolicyAuditEntry[]; total: number };
  },

  /**
   * The seeded baseline for one charter type x framework, or null when the
   * service answered that none exists.
   *
   * Only a JSON 404 means "no baseline". platform-admin-api answers a missing
   * row with a JSON body (collateral-policy.routes.ts GET /baselines); a 404
   * from anything upstream of it -- a load-balancer routing miss returns an
   * HTML page -- is a failed read, and throws so the page can say the read
   * failed instead of reporting that nothing is seeded.
   */
  getBaseline: async (
    charterType: BaselineCharterType,
    regulatoryFramework: BaselineFramework,
  ): Promise<BaselinePolicy | null> => {
    const { data, error, response } = await client.GET('/collateral-policy/baselines', {
      params: { query: { charterType, regulatoryFramework } },
    });
    const status = response.status;
    if (status === 404 && isJsonResponse(response)) return null;
    if (error || !data) {
      throw new Error(
        apiErrorMessage(error) ?? `Baseline read failed (HTTP ${status})`,
      );
    }
    return data;
  },
};

export type BaselineCharterType = components['schemas']['CharterType'];
export type BaselineFramework = NonNullable<components['schemas']['BaselinePolicy']['regulatoryFramework']>;

function isJsonResponse(response: Response): boolean {
  return (response.headers.get('content-type') ?? '').toLowerCase().includes('application/json');
}
